// NEW_FILE_JUSTIFIED: New hook for PolicyFormPage form state management — no existing equivalent
// HOOK_STATE_JUSTIFIED: form state management hook — this is where state is extracted to
import { useState, useCallback } from 'react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need two levels up to reach convex/_generated/
import type { IPolicyFieldValue, TPolicyStatus, TInvoiceStatus } from '@/@types/insurance';

export interface IUsePolicyFormReturn {
  policyNumber: string;
  customerName: string;
  status: TPolicyStatus;
  invoiceStatus: TInvoiceStatus;
  partiallyPaidAmount: number | undefined;
  premium: number | undefined;
  policyStartDate: string;
  policyExpireDate: string;
  policyDuration: string;
  fieldValues: Map<string, IPolicyFieldValue>;
  setPolicyNumber: (value: string) => void;
  setCustomerName: (value: string) => void;
  setStatus: (value: TPolicyStatus) => void;
  setInvoiceStatus: (value: TInvoiceStatus) => void;
  setPartiallyPaidAmount: (value: number | undefined) => void;
  setPremium: (value: number | undefined) => void;
  setPolicyStartDate: (value: string) => void;
  setPolicyExpireDate: (value: string) => void;
  setPolicyDuration: (value: string) => void;
  getFieldValue: (fieldLabel: string) => string;
  getFieldStorageIds: (fieldLabel: string) => Id<'_storage'>[];
  setFieldValue: (fieldLabel: string, value: string) => void;
  addFileToField: (fieldLabel: string, storageId: Id<'_storage'>) => void;
  removeFileFromField: (fieldLabel: string, storageId: Id<'_storage'>) => void;
  toFieldValues: () => IPolicyFieldValue[];
  initFromExisting: (params: {
    policyNumber: string;
    customerName?: string;
    status: TPolicyStatus;
    invoiceStatus?: TInvoiceStatus;
    partiallyPaidAmount?: number;
    premium?: number;
    policyStartDate?: string;
    policyExpireDate?: string;
    policyDuration?: string;
    fieldValues: IPolicyFieldValue[];
  }) => void;
}

export function usePolicyForm(): IUsePolicyFormReturn {
  const [policyNumber, setPolicyNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [status, setStatus] = useState<TPolicyStatus>('draft');
  const [invoiceStatus, setInvoiceStatus] = useState<TInvoiceStatus>('not_paid');
  const [partiallyPaidAmount, setPartiallyPaidAmount] = useState<number | undefined>(undefined);
  const [premium, setPremium] = useState<number | undefined>(undefined);
  const [policyStartDate, setPolicyStartDateRaw] = useState('');
  const [policyExpireDate, setPolicyExpireDateRaw] = useState('');
  const [policyDuration, setPolicyDuration] = useState('');

  const calcDuration = useCallback((start: string, expire: string) => {
    if (!start || !expire) return;
    const s = new Date(start);
    const e = new Date(expire);
    const months = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24 * 30));
    if (months > 0) setPolicyDuration(`${months}_months`);
  }, []);

  const setPolicyStartDate = useCallback(
    (val: string) => {
      setPolicyStartDateRaw(val);
      calcDuration(val, policyExpireDate);
    },
    [policyExpireDate, calcDuration]
  );

  const setPolicyExpireDate = useCallback(
    (val: string) => {
      setPolicyExpireDateRaw(val);
      calcDuration(policyStartDate, val);
    },
    [policyStartDate, calcDuration]
  );
  const [fieldValues, setFieldValues] = useState<Map<string, IPolicyFieldValue>>(new Map());

  const getFieldValue = useCallback(
    (fieldLabel: string): string => {
      return fieldValues.get(fieldLabel)?.value ?? '';
    },
    [fieldValues]
  );

  const getFieldStorageIds = useCallback(
    (fieldLabel: string): Id<'_storage'>[] => {
      return fieldValues.get(fieldLabel)?.storageIds ?? [];
    },
    [fieldValues]
  );

  const setFieldValue = useCallback((fieldLabel: string, value: string) => {
    setFieldValues(prev => {
      const next = new Map(prev);
      const existing = next.get(fieldLabel);
      next.set(fieldLabel, { ...existing, fieldLabel, value });

      // Auto-calculate Expiry date from Start date + Duration (months)
      const startDate = fieldLabel === 'Start date' ? value : (next.get('Start date')?.value ?? '');
      const duration =
        fieldLabel === 'Duration (months)' ? value : (next.get('Duration (months)')?.value ?? '');

      if (startDate && duration) {
        const months = parseInt(duration, 10);
        const start = new Date(startDate);
        if (!isNaN(start.getTime()) && !isNaN(months) && months > 0) {
          const expiry = new Date(start);
          expiry.setMonth(expiry.getMonth() + months);
          expiry.setDate(expiry.getDate() - 1);
          const expiryStr = expiry.toISOString().split('T')[0];
          next.set('Expiry date', {
            ...next.get('Expiry date'),
            fieldLabel: 'Expiry date',
            value: expiryStr,
          });
        }
      }

      return next;
    });
  }, []);

  const addFileToField = useCallback((fieldLabel: string, storageId: Id<'_storage'>) => {
    setFieldValues(prev => {
      const next = new Map(prev);
      const existing = next.get(fieldLabel);
      const storageIds = [...(existing?.storageIds ?? []), storageId];
      next.set(fieldLabel, { ...existing, fieldLabel, storageIds });
      return next;
    });
  }, []);

  const removeFileFromField = useCallback((fieldLabel: string, storageId: Id<'_storage'>) => {
    setFieldValues(prev => {
      const next = new Map(prev);
      const existing = next.get(fieldLabel);
      const storageIds = (existing?.storageIds ?? []).filter(id => id !== storageId);
      next.set(fieldLabel, { ...existing, fieldLabel, storageIds });
      return next;
    });
  }, []);

  const toFieldValues = useCallback((): IPolicyFieldValue[] => {
    return Array.from(fieldValues.values()).filter(
      fv => fv.value !== undefined || (fv.storageIds && fv.storageIds.length > 0)
    );
  }, [fieldValues]);

  const initFromExisting = useCallback(
    (params: {
      policyNumber: string;
      customerName?: string;
      status: TPolicyStatus;
      invoiceStatus?: TInvoiceStatus;
      partiallyPaidAmount?: number;
      premium?: number;
      policyStartDate?: string;
      policyExpireDate?: string;
      policyDuration?: string;
      fieldValues: IPolicyFieldValue[];
    }) => {
      setPolicyNumber(params.policyNumber);
      setCustomerName(params.customerName ?? '');
      setStatus(params.status);
      setInvoiceStatus(params.invoiceStatus ?? 'not_paid');
      setPartiallyPaidAmount(params.partiallyPaidAmount);
      setPremium(params.premium);
      setPolicyStartDateRaw(params.policyStartDate ?? '');
      setPolicyExpireDateRaw(params.policyExpireDate ?? '');
      setPolicyDuration(params.policyDuration ?? '');
      const map = new Map<string, IPolicyFieldValue>();
      for (const fv of params.fieldValues) {
        map.set(fv.fieldLabel, fv);
      }
      setFieldValues(map);
    },
    []
  );

  return {
    policyNumber,
    customerName,
    status,
    invoiceStatus,
    partiallyPaidAmount,
    premium,
    fieldValues,
    setPolicyNumber,
    setCustomerName,
    setStatus,
    setInvoiceStatus,
    setPartiallyPaidAmount,
    setPremium,
    policyStartDate,
    policyExpireDate,
    policyDuration,
    setPolicyStartDate,
    setPolicyExpireDate,
    setPolicyDuration,
    getFieldValue,
    getFieldStorageIds,
    setFieldValue,
    addFileToField,
    removeFileFromField,
    toFieldValues,
    initFromExisting,
  };
}
